import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DragIndicator as DragIndicatorIcon } from '@mui/icons-material';
import { Box, Card, CardContent, Chip, Typography } from '@mui/material';
import React from 'react';
import type { Lesson } from '../../types';

interface LessonCardProps {
  lesson: Lesson;
  isEditable?: boolean;
  showDragHandle?: boolean;
}

export const LessonCard: React.FC<LessonCardProps> = ({
  lesson,
  isEditable = true,
  showDragHandle = true,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: lesson.id,
    disabled: !isEditable,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Get subject color for visual distinction
  const getSubjectColor = (subjectId: string): string => {
    const colors: Record<string, string> = {
      'subject-eng': '#3b82f6', // blue
      'subject-math': '#ef4444', // red
      'subject-sci': '#10b981', // green
      'subject-hist': '#f59e0b', // amber
      'subject-geog': '#8b5cf6', // purple
      'subject-art': '#ec4899', // pink
      'subject-music': '#14b8a6', // teal
      'subject-pe': '#f97316', // orange
      'subject-dt': '#6366f1', // indigo
      'subject-ict': '#06b6d4', // cyan
    };
    return colors[subjectId] || '#64748b'; // slate as default
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      sx={{
        cursor: isEditable ? 'grab' : 'default',
        '&:active': {
          cursor: isEditable ? 'grabbing' : 'default',
        },
        borderLeft: `4px solid ${getSubjectColor(lesson.subjectId)}`,
        height: '100%',
        minHeight: 80,
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          {showDragHandle && isEditable && (
            <Box
              {...attributes}
              {...listeners}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'grab',
                '&:active': { cursor: 'grabbing' },
              }}
            >
              <DragIndicatorIcon fontSize="small" color="action" />
            </Box>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              fontWeight="bold"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {lesson.subjectName}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {lesson.teacherName}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
              <Chip label={lesson.roomName} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.7rem' }} />
              <Chip
                label={lesson.groupName}
                size="small"
                variant="outlined"
                sx={{ height: 18, fontSize: '0.7rem' }}
              />
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
